import React, { useState } from 'react';
import { phaseChipColors, type SimulationPhaseChip } from '../../utils/simulationPhaseUi';
import {
  PortMarker,
  STOCKER_PORT_KINDS,
  TOOL_PORT_KINDS,
} from './SimulationLegendGraphics';

const OHT_PHASE_ITEMS: Array<{
  chip: SimulationPhaseChip;
  label: string;
  description: string;
}> = [
  {
    chip: 'travel',
    label: 'Travel',
    description: 'OHT is moving with a FOUP — picking up or delivering to a tool or stocker.',
  },
  {
    chip: 'deadhead',
    label: 'Deadhead',
    description: 'OHT is moving empty to pick up a FOUP or reposition for the next job.',
  },
  {
    chip: 'idle',
    label: 'Idle',
    description: 'OHT has no active transport leg and is parked or waiting for work.',
  },
  {
    chip: 'wait',
    label: 'Wait',
    description:
      'OHT is stopped — junction dwell, segment clearance, collision avoidance, or process hold.',
  },
];

const LegendSectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{children}</h4>
);

const PortKindRow: React.FC<{
  items: ReadonlyArray<{ color: string; label: string }>;
}> = ({ items }) => (
  <ul className="space-y-1.5">
    {items.map((item) => (
      <li key={item.label} className="flex items-center gap-2.5">
        <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0" aria-hidden>
          <PortMarker x={12} y={10} color={item.color} direction={90} scale={1} />
        </svg>
        <span className="text-[10px] font-semibold text-slate-700">{item.label}</span>
      </li>
    ))}
  </ul>
);

/**
 * Collapsible simulation legend — OHT phases plus tool/stocker port markers.
 */
const SimulationLegend: React.FC = () => {
  const [expanded, setExpanded] = useState(false);

  if (!expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-expanded="false"
        className="absolute left-3 top-3 z-30 flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
      >
        <span className="shrink-0 -rotate-90 text-slate-400 transition-transform" aria-hidden>
          <ChevronIcon />
        </span>
        <span>Legend</span>
      </button>
    );
  }

  return (
    <section className="absolute left-3 top-3 z-30 w-104 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
      <button
        type="button"
        onClick={() => setExpanded(false)}
        aria-expanded="true"
        className="flex w-full items-center gap-1.5 border-b border-slate-100 px-3 py-2.5 text-left"
      >
        <span className="shrink-0 text-slate-400" aria-hidden>
          <ChevronIcon />
        </span>
        <span className="text-xs font-semibold text-slate-800">Legend</span>
      </button>

      <div className="max-h-[calc(100vh-5rem)] space-y-3 overflow-y-auto p-3 scrollbar-thin">
        <section className="space-y-2">
          <LegendSectionTitle>OHT phases</LegendSectionTitle>
          <ul className="space-y-2">
            {OHT_PHASE_ITEMS.map(({ chip, label, description }) => {
              const colors = phaseChipColors(chip);
              return (
                <li key={chip} className="flex items-start gap-2.5">
                  <span
                    className="inline-flex h-4.5 w-19 shrink-0 items-center justify-center rounded px-1.5 text-[10px] font-semibold leading-none"
                    style={{ backgroundColor: colors.bg, color: colors.fg }}
                  >
                    {label}
                  </span>
                  <p className="min-w-0 flex-1 text-[11px] leading-snug text-slate-600">{description}</p>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="grid grid-cols-2 gap-3 border-t border-slate-100 pt-3">
          <div className="space-y-2">
            <LegendSectionTitle>Tool ports</LegendSectionTitle>
            <PortKindRow items={TOOL_PORT_KINDS} />
          </div>
          <div className="space-y-2">
            <LegendSectionTitle>Stocker ports</LegendSectionTitle>
            <PortKindRow items={STOCKER_PORT_KINDS} />
          </div>
        </section>
      </div>
    </section>
  );
};

const ChevronIcon: React.FC = () => (
  <svg
    className="h-3 w-3"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
  >
    <path d="M3 4.5 6 7.5 9 4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default SimulationLegend;
